/**
 * Presentation board for the Naturality Squares arcade game.
 *
 * The board renders fixed square sides and selectable bottom rewrites. The
 * panel owns all state transitions and structural verdicts.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { ArcadeSelectableTile } from "../../../../games/index.js";
import type {
  BoundaryMorphism,
  NaturalitySquare,
} from "../../algorithms/naturalitySquares.js";

interface NaturalitySquareBoardProps {
  readonly armedCandidateId: string | null;
  readonly disabled: boolean;
  readonly onArmCandidate: (candidateId: string) => void;
  readonly square: NaturalitySquare | null;
}

const ACCENT = panelTokens.accentViolet;

export function NaturalitySquareBoard({
  armedCandidateId,
  disabled,
  onArmCandidate,
  square,
}: NaturalitySquareBoardProps): React.JSX.Element {
  if (square === null) {
    return (
      <PanelSurface.Card>
        <PanelSurface.Prose tone="muted">Round cleared.</PanelSurface.Prose>
      </PanelSurface.Card>
    );
  }

  return (
    <section className="relative mt-4 rounded-2xl border border-white/10 bg-black/25 p-3 sm:p-4">
      <SquareDiagram square={square} />
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {square.candidates.map((candidate) => {
          const selected = armedCandidateId === candidate.id;
          return (
            <ArcadeSelectableTile
              key={candidate.id}
              accent={selected ? ACCENT : panelTokens.accentCyan}
              ariaLabel={`Arm bottom rewrite ${candidate.label}`}
              disabled={disabled}
              onClick={() => {
                onArmCandidate(candidate.id);
              }}
              selected={selected}
              title={candidate.label}
              subtitle={permText(candidate)}
              trailing={selected ? "armed" : "bottom"}
              titleColor={panelTokens.bright}
              mutedColor={panelTokens.muted}
              voidColor={panelTokens.elev}
              style={{ minHeight: 70 }}
            />
          );
        })}
      </div>
    </section>
  );
}

function SquareDiagram({
  square,
}: {
  readonly square: NaturalitySquare;
}): React.JSX.Element {
  return (
    <div
      className="mx-auto grid w-full max-w-[560px] min-w-0 items-center justify-items-center gap-x-2 gap-y-2 rounded-2xl border p-3 sm:gap-x-3 sm:p-4"
      style={{
        background: "rgba(5,8,16,0.48)",
        borderColor: `${ACCENT}44`,
        color: panelTokens.text,
        fontFamily: panelTokens.mono,
        gridTemplateColumns: "minmax(58px,1fr) minmax(52px,0.7fr) minmax(58px,1fr)",
        gridTemplateRows: "auto auto auto",
      }}
    >
      <Corner label="A" />
      <MorphismChip
        label="top"
        morphism={square.top}
        color={panelTokens.accentCyan}
      />
      <Corner label="B" />

      <MorphismChip
        label="left"
        morphism={square.left}
        color={panelTokens.accentAmber}
      />
      <span className="text-[22px] text-[var(--cs-muted)]">[]</span>
      <MorphismChip
        label="right"
        morphism={square.right}
        color={panelTokens.accentAmber}
      />

      <Corner label="A'" />
      <span
        className="font-mono text-[12px] font-black uppercase"
        style={{ color: ACCENT }}
      >
        bottom = ?
      </span>
      <Corner label="B'" />
    </div>
  );
}

function MorphismChip({
  label,
  morphism,
  color,
}: {
  readonly label: string;
  readonly morphism: BoundaryMorphism;
  readonly color: string;
}): React.JSX.Element {
  return (
    <span
      className="inline-flex min-w-0 max-w-full flex-col items-center gap-0.5 rounded-lg border px-2 py-1.5 text-center font-mono text-[12px] font-bold sm:px-3"
      style={{
        background: `${color}14`,
        borderColor: `${color}66`,
        color,
      }}
    >
      <span className="max-w-full break-words">
        {label}: {morphism.label}
      </span>
      <span className="text-[10px] opacity-80">{permText(morphism)}</span>
    </span>
  );
}

function Corner({ label }: { readonly label: string }): React.JSX.Element {
  return (
    <span
      className="grid h-9 w-9 place-items-center rounded-full border text-[14px] font-black"
      style={{
        background: "rgba(5,8,16,0.55)",
        borderColor: panelTokens.border,
        color: panelTokens.bright,
      }}
    >
      {label}
    </span>
  );
}

function permText(morphism: BoundaryMorphism): string {
  return `[${morphism.perm.join(" ")}]`;
}
